// infrastructure/api/fundingSourceApi.js
import { auth } from 'boot/firebase';
import { serverTimestamp } from 'firebase/firestore';
import FundingSourcesDB from 'src/fb/fundingSources-db';
import GenericDB from 'src/fb/generic-db';

import { VersionCollision } from '../../application/ports/FundingSourceRepository.port';
import { createVersionTimeline, currentAsOf, scheduledAsOf } from '../../domain/model/VersionTimeline';
import { toAsOfDate } from '../../domain/services/effectiveDating';
import { toDocument, toFundingSource, versionDocumentId } from '../mappers/fundingSourceMapper';

/**
 * Persistence for funding sources.
 *
 * `fundingSources` is a flat root collection scoped by the document's `clinic` field (HR-4574), so the
 * clinic constraint is what scopes a read rather than the path.
 *
 * EVERY DOCUMENT IS A VERSION. A payer is a series of them sharing a `fundingSourceId`, each applying
 * from its `effectiveFrom` onward. The series' first document keeps the auto-id it was created with,
 * because that id is what `clientCoverages`, `clientAuthorizations` and appointments already reference
 * — which is what let versioning arrive without repointing a single stored reference.
 *
 * CONSEQUENCE FOR EVERY READER, and it is the whole cost of this shape: a query over this collection
 * now returns several rows per payer. Anything listing payers has to resolve one row per series first,
 * or it shows the same funder several times. `loadFundingSources` is that resolution for this context;
 * readers elsewhere either call it or run the indexed as-of query themselves.
 *
 * THE INDEXED AS-OF QUERY, for readers resolving a single payer on a service date:
 *
 *   clinic == X AND fundingSourceId == Y AND isLatestVersion == true
 *     AND effectiveFrom <= serviceDate  ORDER BY effectiveFrom DESC  LIMIT 1
 *
 * That is what `isLatestVersion` exists for — one document back instead of a series to reduce. It is
 * not used below: the client-side reduce resolves on `effectiveFrom` and `version` through
 * `VersionTimeline`, so a flag left wrong by a concurrent write cannot corrupt what the editor shows.
 * The flag is a read optimization; the version numbers are the truth.
 *
 * Removal is a SOFT delete via `isDeleted`, and the rules grant no hard delete except for a version
 * that has never been in force.
 */

/**
 * The effective date, normalized, or a thrown error.
 *
 * EVERY WRITE GOES THROUGH THIS, because a version without a readable date is the one malformed
 * document this collection cannot tolerate. `VersionTimeline` drops such a row rather than guessing
 * its position, so it would vanish from the editor while still sitting in the collection — invisible,
 * unresolvable, and impossible to correct through the UI that created it. The editor already refuses
 * to submit without a date; this is the guarantee underneath, for every caller that is not the editor.
 *
 * Thrown rather than defaulted to today. A version dated by accident is worse than a save that failed:
 * the failure is visible and repeatable, and the wrong date silently decides what a past service date
 * resolves to.
 *
 * READ WITH `toAsOfDate`, NOT `toDateStr`, so an instant is refused rather than sliced. Slicing picks
 * whatever zone the string was written in — '2026-01-01T23:45:00.000Z' becomes the 1st, already the
 * 2nd in Tokyo — and the day is encoded in the document id, so nothing downstream could catch it.
 * `toDateStr` is lenient for a stored BOUND that may predate the convention; an `effectiveFrom`
 * reaching a write is caller-supplied. A `Date` was already refused here, and a serialized instant is
 * the same value missing the same timezone.
 *
 * @param {*} effectiveFrom
 * @returns {string} 'YYYY-MM-DD'
 */
const requireCalendarDate = (effectiveFrom) => {
  const dateStr = toAsOfDate(effectiveFrom);

  if (dateStr === null) {
    throw new TypeError(
      `A funding source version needs a 'YYYY-MM-DD' effective date; got ${JSON.stringify(effectiveFrom)}.`,
    );
  }
  return dateStr;
};

/**
 * Tombstone fields — the platform's soft-delete trio, matching `coverageApi` exactly rather than
 * coining a synonym: a retention sweep is cross-collection by nature and one written against the
 * established names would skip a tombstone called anything else.
 *
 * `deleteTimestamp` is a Firestore `Timestamp` and not a 'YYYY-MM-DD' string — the same deliberate
 * exception coverage makes. `effectiveFrom` is a calendar date and must never be an instant, whereas a
 * deletion is a moment, and the clock is the server's so a wrong device clock cannot move a retention
 * window.
 *
 * `auth` ITSELF IS OPTIONAL-CHAINED, not just `currentUser`, matching `coverageApi`. `auth` is a
 * mutable export: switching region tears the Firebase app down and re-initializes it, so it is null
 * for a window. Reaching through it unguarded throws a TypeError and abandons the whole write —
 * losing the cancellation rather than merely losing the name of who made it.
 */
const tombstone = () => ({
  isDeleted: true,
  deleteTimestamp: serverTimestamp(),
  deletedBy: auth?.currentUser?.uid ?? null,
});

/**
 * The as-of date, or a thrown error.
 *
 * REFUSED RATHER THAN DEFAULTED TO TODAY, because the failure this prevents was invisible. A caller
 * that omitted the date got every comparison against `undefined` returning false, so no version
 * resolved, so the single-row fallback took over — and every funder that had ever been amended
 * silently disappeared from the picker. A funder that vanishes is not a visible bug; it looks like
 * configuration nobody added. Defaulting would have hidden it just as well.
 *
 * Read with `toAsOfDate`, not `toDateStr`: the message promises 'YYYY-MM-DD', and the raw value goes
 * on to be a Firestore range bound and the input to every version comparison. A truncating read would
 * let an instant through and make that promise false.
 *
 * @param {*} asOf
 * @param {string} reader - the calling read, so the message says which one was handed what
 */
const requireAsOfDate = (asOf, reader) => {
  if (toAsOfDate(asOf) === null) {
    throw new TypeError(
      `${reader} needs a 'YYYY-MM-DD' as-of date to resolve versions; got ${JSON.stringify(asOf)}.`,
    );
  }
};

/**
 * One row per payer, from version documents belonging to several of them.
 *
 * Still a reduce, because the query cannot collapse a payer's earlier dated versions — Firestore has
 * no "latest per group". `resolve` is the one thing the two reads below disagree about: which version
 * stands for its series.
 *
 * @param {Array<Object>} documents - stored versions, in any order, of any number of payers
 * @param {(timeline: Object) => (Object|null)} resolve - picks the version that represents one series
 * @returns {import('../../domain/model/FundingSource.types').FundingSource[]}
 */
const onePerSeries = (documents, resolve) => {
  const seriesById = new Map();
  documents.map(toFundingSource).forEach((version) => {
    const series = seriesById.get(version.fundingSourceId) ?? [];
    seriesById.set(version.fundingSourceId, [...series, version]);
  });

  return [...seriesById.values()]
    .map((series) => {
      const resolved = resolve(createVersionTimeline(series));

      if (resolved === null || resolved === undefined) {
        return null;
      }
      // The timeline carries coordinates only, so the resolved head is matched back to the record.
      return series.find((version) => version.versionId === resolved.versionId) ?? null;
    })
    .filter((version) => version !== null);
};

/**
 * The clinic's payers, one row per payer, as each stood on the given date.
 *
 * Deliberately not the auth store's cached copy: funding sources are lazily bound there and a client
 * dialog can be opened without them, so this reads the collection directly.
 *
 * `isDeleted` is filtered for the same reason coverage filters it: a tombstone is not inert on this
 * side of the picker — a deleted payer offered here gets selected as the client's payer and billed.
 *
 * THE QUERY NARROWS BEFORE THE REDUCE, and both filters are worth their index. `isLatestVersion`
 * removes a version superseded by a correction ON ITS OWN DATE; `effectiveFrom <= asOf` removes one
 * scheduled for later. Neither can remove a payer's earlier dated versions — Firestore has no
 * "latest per group" — so the reduce below still has work to do, and the read still grows slowly with
 * a payer's history. What the pair buys is that a series with one past date returns one row, which is
 * every payer today.
 *
 * REQUIRES THE BACKFILL TO HAVE RUN IN THIS ENVIRONMENT. Firestore excludes documents that lack a
 * filtered field, so a funder written before versioning — no `effectiveFrom` — is not returned at all.
 * There is deliberately no fallback for that: a fallback would only mask the misordering, and the
 * failure it masks is a payer silently missing from every picker. The migration is step one of the
 * three-step deploy for exactly this reason.
 *
 * @param {string} clinicId
 * @param {string} asOf - 'YYYY-MM-DD'
 * @returns {Promise<import('../../domain/model/FundingSource.types').FundingSource[]>}
 */
export async function loadFundingSources(clinicId, asOf) {
  requireAsOfDate(asOf, 'loadFundingSources');

  const documents = await new FundingSourcesDB().readAll([
    ['clinic', '==', clinicId],
    ['isDeleted', '==', false],
    ['isLatestVersion', '==', true],
    ['effectiveFrom', '<=', asOf],
  ]);

  return onePerSeries(documents, (timeline) => currentAsOf(timeline, asOf));
} // loadFundingSources()

/**
 * EVERY payer the clinic has configured, one row per payer — including one that has not started yet.
 *
 * THE SETTINGS TABLE'S READ, and it is a different question from the one above. `loadFundingSources`
 * answers "which payers apply on this date", which is what a picker needs. A management table answers
 * "which payers exist", and the two disagree about exactly one case: a payer whose earliest version is
 * dated in the future. Creating one is an ordinary thing to do — a contract that starts next month —
 * and under the picker's question it is filtered out twice, by the range bound in the query and by
 * `currentAsOf` returning null. The row the user just created was invisible on the page that created
 * it, so there was no way to open, correct or continue it (HR-4574).
 *
 * NO RANGE BOUND ON THE QUERY, therefore, and the cost is real but small: a payer's future-dated
 * versions come back too, so the read grows with a clinic's scheduled changes rather than with its
 * payers. Bounded by `isLatestVersion`, one row per distinct effective date, on a settings page that
 * loads once. The remaining filters are all equalities, which Firestore serves from its single-field
 * indexes — the composite index exists for the bounded query above and is untouched.
 *
 * A PAYER STILL RESOLVES TO ONE VERSION, the one in force, and only falls back to its earliest
 * scheduled one when nothing is in force at all. A payer that has started is shown as it stands today,
 * never as it will stand next month; its scheduled change belongs to the editor's version line, not to
 * a status column. The fallback is what makes the not-yet-started payer visible, and the row presenter
 * has to label it as scheduled rather than as active — see `presentFundingSourceRow`, which takes the
 * same `asOf` for that reason.
 *
 * @param {string} clinicId
 * @param {string} asOf - 'YYYY-MM-DD'
 * @returns {Promise<import('../../domain/model/FundingSource.types').FundingSource[]>}
 */
export async function loadConfiguredFundingSources(clinicId, asOf) {
  requireAsOfDate(asOf, 'loadConfiguredFundingSources');

  const documents = await new FundingSourcesDB().readAll([
    ['clinic', '==', clinicId],
    ['isDeleted', '==', false],
    ['isLatestVersion', '==', true],
  ]);

  return onePerSeries(
    documents,
    (timeline) => currentAsOf(timeline, asOf) ?? scheduledAsOf(timeline, asOf)[0] ?? null,
  );
} // loadConfiguredFundingSources()

/**
 * Creates a payer — its first version, on the date the user chose.
 *
 * NOT an append, and it cannot be one: `appendFundingSourceVersion` places a version on an existing
 * series, and there is no series yet. There is also nothing to collide with, so no transaction and no
 * derived id are needed.
 *
 * THE ID IS GENERATED LOCALLY AND USED TWICE — as the document id and as `fundingSourceId`. That is
 * what keeps every payer the same shape: the series key is the first document's id, for a funder
 * created today exactly as for one the backfill touched. Generating it up front rather than letting
 * Firestore assign one is what makes writing both in a single create possible.
 *
 * @param {{ clinicId: string, effectiveFrom: string, record: Object }} request - `effectiveFrom` is
 *   the first version's date, and it is the user's answer rather than today: a payer whose terms
 *   started in January is an ordinary thing to be entering now, and with no earlier version there is
 *   nothing for that date to conflict with.
 * @returns {Promise<string>} the new payer's `fundingSourceId`
 */
export async function createFundingSource({ clinicId, effectiveFrom, record }) {
  const startsOn = requireCalendarDate(effectiveFrom);
  const fundingSourcesDb = new FundingSourcesDB();
  const fundingSourceId = fundingSourcesDb.newId();

  await fundingSourcesDb.create({
    ...toDocument(record),
    fundingSourceId,
    clinic: clinicId,
    effectiveFrom: startsOn,
    version: 1,
    isLatestVersion: true,
    isDeleted: false,
  }, fundingSourceId);

  return fundingSourceId;
} // createFundingSource()

/**
 * The WHOLE series for one payer, tombstones included.
 *
 * Tombstones are deliberately NOT filtered, which is the opposite of every other read here. Version
 * numbers are encoded in the document id and must never be re-used, so the next number has to count
 * removed versions too — a reader that filtered them would compute an id that is already occupied and
 * the write would be refused. Resolution excludes them instead, downstream in `VersionTimeline`.
 *
 * @param {{ fundingSourceId: string, clinicId: string }} scope
 * @returns {Promise<import('../../domain/model/FundingSource.types').FundingSource[]>}
 */
export async function loadFundingSourceVersions({ fundingSourceId, clinicId }) {
  const documents = await new FundingSourcesDB().readAll([
    ['clinic', '==', clinicId],
    ['fundingSourceId', '==', fundingSourceId],
  ]);

  return documents.map(toFundingSource);
} // loadFundingSourceVersions()

/**
 * Appends one version, with everything else the same save has to change, in a single transaction.
 *
 * THE TRANSACTION EXISTS FOR THE READ, not for the writes. Its one read is the document this save
 * intends to create, looked up by the id derived from its coordinates — which is the only form of read
 * that works here: the Firestore web SDK's `transaction.get` takes a DocumentReference, and
 * `GenericDB`'s transactional `readAll` runs `getDocs` OUTSIDE the transaction before re-reading each
 * hit through it, so a document created concurrently is invisible to both writers and neither detects
 * the other. A version number computed from a query inside a transaction is therefore not protected by
 * it, however transactional the call looks.
 *
 * THE RULES ARE THE REAL GUARD, and this check is what makes their refusal legible. A losing writer
 * aims at a document that now exists, so `transaction.set` is evaluated as an update and denied —
 * arriving as `permission-denied`, indistinguishable from "you are not a manager at this clinic".
 * Reading first turns the recoverable case into a `VersionCollision` a retry loop can act on. It also
 * protects the one invariant nothing else can: were both writes to land, two rows for one effective
 * date would carry `isLatestVersion`, and the as-of query ends in `LIMIT 1`.
 *
 * THREE KINDS OF WRITE, ONE DECISION. The new version; the flag cleared on whatever it supersedes at
 * the same date; and tombstones for a scheduled change on another date that this save replaces. Landing
 * the version without clearing the flag leaves the as-of query returning either row for that date.
 *
 * `executeTransaction` also carries the offline path and the IndexedDB mirror, which a hand-rolled
 * `writeBatch` would lose. It must be called on a plain GenericDB, not a subclass.
 *
 * @param {import('../../application/ports/FundingSourceRepository.port').AppendVersionRequest} request
 * @returns {Promise<string>} the new version's document id
 */
export function appendFundingSourceVersion({
  fundingSourceId,
  clinicId,
  effectiveFrom,
  version,
  record,
  supersededVersionIds = [],
  cancelledVersionIds = [],
}) {
  const startsOn = requireCalendarDate(effectiveFrom);
  const fundingSourcesDb = new FundingSourcesDB();
  // Built from the NORMALIZED date, so the id and the field can never disagree about when this
  // version applies.
  const id = versionDocumentId(fundingSourceId, startsOn, version);

  return new GenericDB().executeTransaction(async (transaction) => {
    // Reads before writes, per Firestore's transaction rule — and there is only one.
    const occupied = await transaction.read(fundingSourcesDb, { id });

    // BOTH EMPTY VALUES, because the two backends disagree about what "missing" is: the Firestore
    // path returns null, the IndexedDB path hands back `undefined` straight from the store. A bare
    // `!== null` therefore reads every offline document as occupied, and since the caller retries a
    // collision until it gives up, no version could ever be appended offline.
    if (occupied !== null && occupied !== undefined) {
      throw new VersionCollision(startsOn, version);
    }

    await transaction.create(fundingSourcesDb, {
      data: {
        ...toDocument(record),
        fundingSourceId,
        clinic: clinicId,
        effectiveFrom: startsOn,
        version,
        isLatestVersion: true,
        isDeleted: false,
      },
      id,
    });

    // Bookkeeping, not a decision: an earlier entry for this same date is still true history, it is
    // simply no longer the one to read. It keeps its row and loses only the flag.
    await Promise.all(supersededVersionIds.map((supersededId) => {
      return transaction.update(fundingSourcesDb, {
        data: { id: supersededId, isLatestVersion: false },
      });
    }));

    // A decision the user was warned about: a change scheduled for another date is canceled outright.
    // Only versions that have never been in force reach here — the rules refuse the rest.
    await Promise.all(cancelledVersionIds.map((cancelledId) => {
      return transaction.update(fundingSourcesDb, { data: { id: cancelledId, ...tombstone() } });
    }));

    return id;
  });
} // appendFundingSourceVersion()
